import { makeAutoObservable, runInAction } from 'mobx';

import { addPayType, deletePayType, getPayTypes, updatePayType } from '../services/PayTypesAPI';
import { PayType, PayTypeModel } from '../models/PayType';
import { errorStore } from './ErrorStore';

class PayTypeStore {
	data: PayType[] = [];
	isLoading: boolean = false;
	showModal = false;
	currentRow: PayTypeModel | undefined;

	constructor() {
		makeAutoObservable(this);
	}

	async loadData() {
		if (this.data.length || this.isLoading) return;

		try {
			this.setLoading(true);
			const response = await getPayTypes();
			this.setData(response || []);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async delete(payType: PayType) {
		try {
			this.setLoading(true);
			await deletePayType(payType.id);
			this.setData(this.data.filter(v => v.id !== payType.id));
			this.clearForm();
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async update(payType: PayType) {
		try {
			this.setLoading(true);
			await updatePayType(payType);

			runInAction(() => {
				this.data = this.data.map(pt =>
					pt.id === payType.id ? { ...payType } : pt
				);
			});

			this.removeCurrent();
		} catch (error: any) {
			this.setError(error.message);
			throw error;
		} finally {
			this.setLoading(false);
		}
	}

	setupNew() {
		this.currentRow = new PayTypeModel({ id: 0, name: '', isActive: true });
		this.setShowModal(true);
	}

	async addNew(payType: PayType, onSuccess?: () => void) {
		this.setLoading(true);
		try {
			const addedNewEpicType = await addPayType(payType);
			if (addedNewEpicType) {
				this.addRow(addedNewEpicType);
				this.clearForm();
				onSuccess?.();
			}
		} catch (error: any) {
			this.setError(error.message)
		} finally {
			this.setLoading(false);
		}
	}

	setLoading(loading: boolean) {
		this.isLoading = loading;
	}

	addRow(payType: PayType) {
		this.data = [payType, ...this.data];
	}

	setError(error: string) {
		errorStore.report(error);
	}

	setShowModal(state: boolean) {
		this.showModal = state;
	}

	removeCurrent() {
		this.currentRow = undefined;
	}

	clearForm() {
		this.removeCurrent();
		this.setShowModal(false);
	}

	setData(payTypes: PayType[]) {
		this.data = payTypes;
	}
}

export const payTypeStore = new PayTypeStore();

